/**
 * Core Logic for Query Parser
 */

"use strict";

import { 
    debugMode, BOOST_EXACT_PHRASE, BOOST_ROMAN_NUMERAL, 
    BOOST_SYNONYM, BOOST_REGEX, BOOST_REGULAR_TERM, BOOST_EXACT_PHRASE_AUTO,
    UNI_B_START, UNI_B_END 
} from './constants.js';
import { processWildcards, arabicToRoman, isStandaloneNumber } from './utils.js';
import { isRegexPattern, extractRegexPattern } from './regex-logic.js';
import { SYNONYM_CONFIG } from './synonyms.js';
import { escapeRegExp } from '../../utils/regex-utils.js';

// Cache for deduplication - avoid logging same query multiple times
let lastParsedQuery = null;
let lastLoggedStructureQuery = null;

// Pre-compiled synonym match-regexes (keys of SYNONYM_CONFIG never change at runtime).
// Without this cache, findSynonyms() recompiles ~22 regexes per term per keystroke.
const _synonymMatchRegexCache = new Map();
function getSynonymMatchRegex(matchPattern) {
    if (_synonymMatchRegexCache.has(matchPattern)) return _synonymMatchRegexCache.get(matchPattern);
    const useUnicode = matchPattern.includes('\\p') || matchPattern.includes('(?<');
    let rx = null;
    try {
        rx = new RegExp(matchPattern, useUnicode ? 'ui' : 'i');
    } catch (e) {
        if (debugMode === '1') console.warn('findSynonyms regex error:', matchPattern, e);
    }
    _synonymMatchRegexCache.set(matchPattern, rx);
    return rx;
}

/**
 * Main entry point for query parsing logic
 */
export function getMatchConditions(q, strictIssue = false) {
    // Compact logging - only log if query changed
    const queryTrimmed = (q && typeof q === 'string') ? q.trim() : '';
    const queryChanged = queryTrimmed && queryTrimmed !== lastParsedQuery;
    if (debugMode === '1' && queryChanged) {
        console.log('[query-parser] Parsing:', queryTrimmed);
        lastParsedQuery = queryTrimmed;
    }

    // 1. Handle special regex pattern format (r"...")
    const regexParse = parseInitialRegex(q);
    if (regexParse.isOnlyRegex) return regexParse.conditions;

    // 2. Extract quoted phrases and exclusions
    const { quotedPhrases, quotedExclusions, remainingQuery } = extractQuotedTerms(q, regexParse.hasRegex);

    // 3. Process positive terms and exclusions
    let searchStrSplit = [];
    let exclusionConditionsList = [];
    
    // Add initial regex if present
    if (regexParse.hasRegex) {
        if (regexParse.isNegative) {
            exclusionConditionsList.push({ "val": regexParse.pattern, "strict": false, "isRegex": true, "flags": regexParse.flags });
        } else {
            searchStrSplit.push({ "val": { pattern: regexParse.pattern, boost: BOOST_REGEX, flags: regexParse.flags }, "strict": false, "isExclusion": false });
        }
    }

    // Process positive quoted phrases
    quotedPhrases.forEach(phrase => {
        const pattern = buildPhrasePattern(phrase);
        searchStrSplit.push({ "val": pattern, "strict": true, "isExclusion": false, "boost": BOOST_EXACT_PHRASE });
    });

    // Process quoted exclusions
    quotedExclusions.forEach(phrase => {
        const pattern = buildPhrasePattern(phrase);
        exclusionConditionsList.push({ "val": pattern, "strict": true });
    });

    // 4. Process remaining query terms
    const termResults = processRemainingTerms(remainingQuery, quotedPhrases, quotedExclusions);
    searchStrSplit.push(...termResults.searchStrSplit);
    exclusionConditionsList.push(...termResults.exclusionConditionsList);

    if (searchStrSplit.length === 0 && exclusionConditionsList.length === 0) {
        return [{ "condition": "and", "value": [], "isCategory": false }];
    }

    // 5. Build final conditions
    const searchConditionsList = buildPositiveConditions(searchStrSplit);
    const exclusionConditionsProcessed = buildExclusionConditions(exclusionConditionsList);
    
    // 6. Final assembly
    const result = assembleFinalResult(searchConditionsList, exclusionConditionsProcessed, termResults.exactPhraseParts);
    
    // Detailed logging: show structure with explanations (only if query changed)
    const structureQueryChanged = queryTrimmed && queryTrimmed !== lastLoggedStructureQuery;
    if (debugMode === '1' && structureQueryChanged) {
        logPatternsStructure(result, termResults.exactPhraseParts);
        lastLoggedStructureQuery = queryTrimmed;
    }
    
    return result;
}

// Helper to log patterns structure with explanations
function logPatternsStructure(result, exactPhraseParts) {
    console.log('[query-parser] ===== PATTERNS STRUCTURE =====');
    console.log('[query-parser] How it works: Both patterns work simultaneously via OR condition');
    console.log('[query-parser] HIGH PRIORITY (boost 5) results appear first, LOW PRIORITY (lower boost) appear below');
    
    // Extract exact phrase patterns (boost 5)
    const exactPatterns = [];
    // Extract individual term patterns (lower boost)
    const termPatterns = [];
    
    function extractFromValue(value) {
        if (Array.isArray(value)) {
            value.forEach(item => {
                if (typeof item === 'object' && item !== null) {
                    // Check if this is a pattern object
                    if (item.pattern) {
                        const boost = item.boost || 1;
                        const pattern = item.pattern;
                        if (boost >= BOOST_EXACT_PHRASE_AUTO) {
                            exactPatterns.push({ pattern, boost });
                        } else {
                            termPatterns.push({ pattern, boost });
                        }
                    }
                    // Recursively check nested value arrays
                    if (item.value && Array.isArray(item.value)) {
                        extractFromValue(item.value);
                    }
                }
            });
        }
    }
    
    // Traverse result structure
    if (Array.isArray(result)) {
        result.forEach(condition => {
            if (condition && condition.value) {
                extractFromValue(condition.value);
            }
        });
    }
    
    // Log exact phrase (high priority pattern)
    if (exactPatterns.length > 0) {
        console.log('[query-parser] HIGH PRIORITY (exact phrase, boost 5) - exact phrase with word boundaries, appears first in results:');
        exactPatterns.forEach((p, idx) => {
            console.log(`[query-parser]   ${idx + 1}. "${p.pattern}" (boost:${p.boost})`);
        });
        if (exactPhraseParts && exactPhraseParts.length > 0) {
            console.log(`[query-parser]   Built from parts: [${exactPhraseParts.join(', ')}]`);
            console.log(`[query-parser]   Includes: synonyms, Roman numerals (6->VI), word boundaries`);
        }
    } else {
        console.log('[query-parser] HIGH PRIORITY: No exact phrase pattern created');
    }
    
    // Log individual terms (low priority patterns)
    if (termPatterns.length > 0) {
        console.log('[query-parser] LOW PRIORITY (individual terms, lower boost) - each word separately, appears below in results:');
        termPatterns.forEach((p, idx) => {
            const boostDesc = p.boost === BOOST_ROMAN_NUMERAL ? 'Roman numeral' : p.boost === BOOST_SYNONYM ? 'Synonym' : 'Regular term';
            console.log(`[query-parser]   ${idx + 1}. "${p.pattern}" (boost:${p.boost}, ${boostDesc})`);
        });
        console.log(`[query-parser]   Works simultaneously with HIGH PRIORITY, results sorted by boost value`);
    } else {
        console.log('[query-parser] LOW PRIORITY: No individual term patterns');
    }
    
    console.log('[query-parser] ===============================');
}

// Helper to extract patterns for compact logging (kept for compatibility)
function extractPatternsFromResult(result) {
    const patterns = [];
    if (!result || !Array.isArray(result)) return patterns;
    
    function traverse(cond) {
        if (!cond || typeof cond !== 'object') return;
        if (cond.pattern) {
            const boost = cond.boost || 1;
            patterns.push(`${cond.pattern}${boost > 1 ? ` (boost:${boost})` : ''}`);
        }
        if (cond.value && Array.isArray(cond.value)) {
            cond.value.forEach(traverse);
        }
    }
    
    result.forEach(traverse);
    return patterns;
}

/**
 * Handle initial regex pattern check (r"...")
 */
function parseInitialRegex(q) {
    if (!q) return { isOnlyRegex: false };
    
    let hasRegex = false;
    let pattern = null;
    let isNegative = false;
    let flags = 'i';
    
    const negativeRQuotedMatch = q.match(/^-r([gimsu]*)(["'])(.+)\2$/);
    if (negativeRQuotedMatch) {
        pattern = negativeRQuotedMatch[3];
        flags = negativeRQuotedMatch[1] || '';
        isNegative = true;
        hasRegex = true;
    } else {
        const rQuotedMatch = q.match(/^r([gimsu]*)(["'])(.+)\2$/);
        if (rQuotedMatch) {
            pattern = rQuotedMatch[3];
            flags = rQuotedMatch[1] || '';
            hasRegex = true;
        }
    }

    if (hasRegex) {
        const remainingAfterRegex = q.replace(/^-?r[gimsu]*(["'])(.+)\1$/, '').trim();
        if (!remainingAfterRegex) {
            const patternObj = { pattern, boost: BOOST_REGEX, flags };
            const condition = isNegative ? "not" : "and";
            return {
                isOnlyRegex: true,
                conditions: [{ 
                    "condition": condition, 
                    "value": [{ "condition": "or", "value": [patternObj], "modifier": flags }], 
                    "isCategory": false 
                }]
            };
        }
    }

    return { hasRegex, pattern, isNegative, flags, isOnlyRegex: false };
}

/**
 * Extract quoted terms and separate from remaining query
 */
function extractQuotedTerms(q, hasRegex) {
    if (!q) return { quotedPhrases: [], quotedExclusions: [], remainingQuery: '' };
    
    let quotedPhrases = [];
    let quotedExclusions = [];
    let remainingQuery = q.trim();
    
    remainingQuery = remainingQuery.replace(/(\d)[\-\.,/](\d)/g, '$1 $2');
    remainingQuery = remainingQuery.replace(/(\d)[\-\.,/]([a-zA-Zа-яА-ЯёЁ])/g, '$1 $2');
    remainingQuery = remainingQuery.replace(/([a-zA-Zа-яА-ЯёЁ])[\-\.,/](\d)/g, '$1 $2');

    let qForQuotes = q;
    if (hasRegex) {
        qForQuotes = q.replace(/^-?r[gimsu]*(["'])(.+)\1$/, '').trim();
    }
    
    const quotePattern = /-?"([^"]+)"/g;
    let quoteMatch;
    const quoteMatches = [];
    
    quotePattern.lastIndex = 0;
    while ((quoteMatch = quotePattern.exec(qForQuotes)) !== null) {
        quoteMatches.push({
            match: quoteMatch[0],
            phrase: quoteMatch[1],
            isExclusion: quoteMatch[0].startsWith('-'),
            start: quoteMatch.index,
            end: quoteMatch.index + quoteMatch[0].length
        });
    }
    
    for (let i = quoteMatches.length - 1; i >= 0; i--) {
        const qm = quoteMatches[i];
        if (qm.isExclusion) quotedExclusions.push(qm.phrase);
        else quotedPhrases.push(qm.phrase);
        remainingQuery = remainingQuery.substring(0, qm.start) + ' ' + remainingQuery.substring(qm.end);
    }

    return { quotedPhrases, quotedExclusions, remainingQuery: remainingQuery.replace(/\\/g, '/').trim() };
}

/**
 * Build regex pattern for a phrase with word boundaries
 */
function buildPhrasePattern(phrase) {
    const processed = processWildcards(phrase);
    const escaped = escapeRegExp(processed);
    const pattern = escaped.replace(/\s+/g, '[\\s\\-\\.,;:]+');
    return UNI_B_START + pattern + UNI_B_END;
}

/**
 * Process remaining terms after quotes are extracted
 */
function processRemainingTerms(remainingQuery, quotedPhrases, quotedExclusions) {
    let searchStrSplit = [];
    let exclusionConditionsList = [];
    if (!remainingQuery) return { searchStrSplit, exclusionConditionsList, exactPhraseParts: [] };
    
    const exactPhraseParts = remainingQuery.split(/\s+/).filter(p => p && !p.startsWith('-'));

    const parts = remainingQuery.split(/\s+/);
    const allowShortGlobal = !!window.__ALLOW_SHORT_TOKENS__;
    const hasLongToken = checkLongTokens(parts, quotedPhrases, quotedExclusions);
    const allowShort = allowShortGlobal || hasLongToken;

    parts.forEach(part => {
        part = part.trim();
        if (!part) return;

        if (/^-\S/.test(part)) {
            const exclusionTerm = part.substring(1).trim();
            if (exclusionTerm && (exclusionTerm.length > 1 || allowShort)) {
                exclusionConditionsList.push({ "val": escapeRegExp(processWildcards(exclusionTerm)), "strict": false });
            }
        } else if (part.length > 1 || allowShort) {
            if (part.search(/^"([^"]+)"$/) > -1) {
                const unquoted = part.replace(/^"([^"]+)"$/, '$1');
                searchStrSplit.push({ "val": buildPhrasePattern(unquoted), "strict": true, "isExclusion": false });
            } else {
                searchStrSplit.push({ "val": buildRegularTermValue(part), "strict": false, "isExclusion": false });
            }
        }
    });

    return { searchStrSplit, exclusionConditionsList, exactPhraseParts };
}

function checkLongTokens(parts, quotedPhrases, quotedExclusions) {
    const hasLongQuoted = [...quotedPhrases, ...quotedExclusions].some(p => p && p.trim().length > 1);
    const hasLongPart = parts.some(part => {
        const trimmed = part ? part.trim() : '';
        if (!trimmed) return false;
        const checkPart = trimmed.startsWith('-') ? trimmed.substring(1).trim() : trimmed;
        return checkPart.replace(/^"([^"]+)"$/, '$1').trim().length > 1;
    });
    return hasLongQuoted || hasLongPart;
}

function buildRegularTermValue(part) {
    let termPattern, regexFlags = 'i';
    const isRegex = isRegexPattern(part);
    if (isRegex) {
        const info = extractRegexPattern(part);
        termPattern = info.pattern;
        regexFlags = info.flags;
    } else {
        termPattern = escapeRegExp(processWildcards(part));
    }

    const boost = isRegex ? BOOST_REGEX : BOOST_REGULAR_TERM;

    const num = isStandaloneNumber(part);
    if (num !== null) {
        const roman = arabicToRoman(num);
        const boundedPattern = UNI_B_START + termPattern + UNI_B_END;
        if (roman) {
            return [
                { pattern: UNI_B_START + roman + UNI_B_END, boost: BOOST_ROMAN_NUMERAL, flags: regexFlags },
                { pattern: boundedPattern, boost: boost, flags: regexFlags }
            ];
        }
        return { pattern: boundedPattern, boost: boost, flags: regexFlags };
    }
    return { pattern: termPattern, boost: boost, flags: regexFlags };
}

/**
 * Build positive conditions from processed search terms
 */
function buildPositiveConditions(searchStrSplit) {
    let searchConditionsList = [];
    searchStrSplit.forEach((searchStr, idx) => {
        const val = searchStr.val;
        const baseBoost = searchStr.boost || BOOST_REGULAR_TERM;
        let patternsToProcess = Array.isArray(val) ? val : (val.pattern ? [val] : [{ pattern: String(val), boost: baseBoost }]);
        
        let searchList = [];
        patternsToProcess.forEach(patternObj => {
            const pattern = patternObj.pattern;
            const boost = patternObj.boost || BOOST_REGULAR_TERM;
            const flags = patternObj.flags || 'i';
            
            const hasBoundaries = pattern.includes(UNI_B_START) || pattern.includes(UNI_B_END) || pattern.includes('\\b');
            if (hasBoundaries || searchStr.strict) {
                searchList.push({ pattern: pattern, boost, flags });
            } else {
                const correctedPattern = applyTypeInCorrections(pattern);
                const synonymData = findSynonyms(pattern, flags);
                if (synonymData) {
                    searchList.unshift({ pattern: synonymData.pattern, boost: BOOST_SYNONYM, flags: synonymData.flags });
                    const boundedOriginal = UNI_B_START + correctedPattern + UNI_B_END;
                    searchList.push({ pattern: boundedOriginal, boost: BOOST_REGULAR_TERM, flags });
                } else {
                    searchList.push({ pattern: correctedPattern, boost, flags });
                }
            }
        });

        if (searchList.length > 0) {
            const firstFlags = searchList[0].flags;
            const finalModifier = (typeof firstFlags === 'string' && firstFlags !== '') ? firstFlags : 'i';
            searchConditionsList.push({ "condition": "or", "value": searchList, "modifier": finalModifier });
        }
    });
    return searchConditionsList;
}

function applyTypeInCorrections(pattern) {
    if (!pattern || typeof pattern !== 'string' || !pattern.replaceMulti) return pattern;
    return pattern.replaceMulti({
        '^(?:ид|ed)$': 'ED/|/ИД/',
        '^(?:pab|бпо)$': `/PAB|/БПО|PAB(?=${UNI_B_END}|\\d)|БПО`,
        '^(?:ьз3)$': 'ьз3|mp3',
        '^(?:зва)$': 'зва|pdf',
        '^(?:вщс|docx?)$': 'вщс|docx?'
    });
}

function findSynonyms(pattern, defaultFlags) {
    if (!pattern) return null;
    for (const [matchPattern, config] of Object.entries(SYNONYM_CONFIG)) {
        const matchRegex = getSynonymMatchRegex(matchPattern);
        if (!matchRegex) continue;
        if (matchRegex.test(pattern)) {
            return {
                pattern: config.pattern,
                flags: (config.flags !== undefined && config.flags !== null) ? config.flags : defaultFlags
            };
        }
    }
    return null;
}

/**
 * Build exclusion conditions
 */
function buildExclusionConditions(exclusionList) {
    return exclusionList.map(excl => {
        let pattern = excl.val ? excl.val.toString() : '';
        const isRegex = excl.isRegex === true;
        const flags = excl.flags || 'i';
        
        return { "condition": "or", "value": isRegex ? [{ pattern, flags }] : [pattern], "modifier": flags };
    });
}

/**
 * Assemble final result structure
 * CRITICAL: Always create exact phrase pattern first (as if user put query in quotes)
 * with boost 5, word boundaries, synonyms and Roman numerals substitution
 */
function assembleFinalResult(searchConditions, exclusions, exactPhraseParts) {
    const result = [];
    if (!exactPhraseParts) exactPhraseParts = [];
    
    let autoExact = [];

    // ALWAYS create exact phrase pattern from user query (even for single word)
    // This is the primary pattern with boost 5, word boundaries, synonyms and Roman numerals
    if (exactPhraseParts.length > 0) {
        const smartParts = exactPhraseParts.map(p => {
            const corrected = applyTypeInCorrections(p);
            const synonymData = findSynonyms(p, 'i');
            const num = isStandaloneNumber(p);
            const roman = num !== null ? arabicToRoman(num) : null;
            
            const variants = [escapeRegExp(p)];
            if (corrected !== p && !corrected.includes('|')) variants.push(escapeRegExp(corrected));
            else if (corrected !== p) variants.push(corrected);
            
            if (synonymData) variants.push(synonymData.pattern);
            if (roman) variants.push(escapeRegExp(roman));
            
            const uniqueVariants = [...new Set(variants)];
            return uniqueVariants.length > 1 ? `(?:${uniqueVariants.join('|')})` : uniqueVariants[0];
        });

        // Join parts with separator pattern and add word boundaries
        const separator = exactPhraseParts.length > 1 ? '[\\s\\-\\.,;:]+' : '';
        const pattern = UNI_B_START + smartParts.join(separator) + UNI_B_END;
        autoExact.push({ pattern: pattern, boost: BOOST_EXACT_PHRASE_AUTO });
    }

    // Structure: both patterns work simultaneously via OR condition
    // High priority (boost 5) results appear first, low priority (lower boost) appear below
    if (autoExact.length > 0 && searchConditions.length > 0) {
        result.push({
            "condition": "or",
            "value": [
                // High priority: exact phrase pattern (boost 5) - appears first in results
                { "condition": "and", "value": [{ "condition": "or", "value": autoExact, "modifier": "i" }], "isCategory": false },
                // Low priority: individual terms (lower boost) - appears below in results
                { "condition": "and", "value": searchConditions, "isCategory": false }
            ],
            "isCategory": false
        });
    } else if (autoExact.length > 0) {
        // Only exact phrase, no individual terms
        result.push({ "condition": "and", "value": [{ "condition": "or", "value": autoExact, "modifier": "i" }], "isCategory": false });
    } else if (searchConditions.length > 0) {
        // Only individual terms (shouldn't happen if exactPhraseParts.length > 0)
        result.push({ "condition": "and", "value": searchConditions, "isCategory": false });
    } else if (exclusions && exclusions.length > 0) {
        result.push({ "condition": "and", "value": [], "isCategory": false });
    }

    if (exclusions && exclusions.length > 0) {
        result.push({ "condition": "not", "value": exclusions, "isCategory": false });
    }

    return result.length === 0 ? [{ "condition": "and", "value": [], "isCategory": false }] : result;
}
